import { IApiSmpGptAgent } from "@smp/api";
import { dataArraySieve } from "@smp/utils";

import {
  GPT_LANGUAGES,
  GPT_POST_EXTRA_FIELDS,
  GPT_POST_FRAMEWORKS,
  GPT_TEXT_LENGHT_PRESETS,
  GPT_TEXT_STYLES,
} from "@/fixtures";

const getFrameworkDisplayName = (frameworks: string[] | undefined) => {
  const knownFrameworks = GPT_POST_FRAMEWORKS.filter(
    ({ id }) => id !== "random",
  );

  const hasRandomFramework = frameworks?.some((id) => id === "random");

  if (hasRandomFramework) {
    let knownSelectedFrameworks = knownFrameworks.filter((framework) =>
      frameworks?.includes(framework.id),
    );

    knownSelectedFrameworks = knownSelectedFrameworks.length
      ? knownSelectedFrameworks
      : knownFrameworks;

    return `Случайный из ${knownSelectedFrameworks
      .map((framework) => framework.getDisplayName())
      .join(", ")}`;
  }

  if (frameworks?.length) {
    return GPT_POST_FRAMEWORKS.filter(({ id }) => frameworks.includes(id))
      .map((framework) => {
        if (framework.id === "random") {
          return `${framework.getDisplayName()} из ${knownFrameworks
            .map(({ getDisplayName }) => getDisplayName())
            .join(", ")}`;
        }

        return framework.getDisplayName();
      })
      .join(", ");
  }
};

const serializePostParams = (params: IApiSmpGptAgent["params"]) => {
  const postLengt = GPT_TEXT_LENGHT_PRESETS.find(
    ({ id }) => id === (params?.post?.text_length || "medium"),
  )?.getDisplayName();

  const postStyles = GPT_TEXT_STYLES.filter(({ id }) =>
    (params?.post?.text_style || []).includes(id),
  )
    .map((style) => style.getDisplayName())
    .join(", ");

  const postExtraFields = GPT_POST_EXTRA_FIELDS.filter(({ id }) =>
    (params?.post?.extra_fields || []).includes(id),
  )
    .map((field) => field.getDisplayName())
    .join(", ");

  const postFramework = getFrameworkDisplayName(params?.post?.frameworks);

  const postLanguage = GPT_LANGUAGES.find(
    ({ id }) => id === (params?.post?.language || "ru"),
  )?.getDisplayName();

  return {
    post_num: params?.post?.num,
    text_length: postLengt,
    text_style: postStyles,
    extra_fields: postExtraFields,
    framework: postFramework,
    language: postLanguage,
  } as const;
};

export const generateAgentContext = (params: IApiSmpGptAgent["params"]) => {
  const postParams = serializePostParams(params);

  const contextParamsString =
    params?.context &&
    dataArraySieve(
      (
        Object.keys(params.context) as Array<
          keyof NonNullable<NonNullable<typeof params>["context"]>
        >
      ).map((key) => {
        const value = params?.context?.[key];
        const label = {
          occupation: "Чем вы занимаетесь?",
          target_audience: "Кто ваш клиент? ",
          problem: "Какие проблемы клиента решает ваш бизнес?",
          usp: "Ваше УТП",
          benefits: "Чем вы отличаетесь от конкурентов?",
          goal: "Ваша цель",
          key_messages: "Ключевые сообщения",
          avoided_topics: "Какие темы следует избегать?",
          sample: "Образец текста для подражания оригинальному стилю",
        }[key];

        if (!value || !label) {
          return undefined;
        }

        return `${label}\n${value}`;
      }),
    ).join("\n");

  const postParamsString = dataArraySieve(
    (Object.keys(postParams) as Array<keyof typeof postParams>).map((key) => {
      const value = postParams[key];
      const label = {
        text_length: "Длина постов",
        text_style: "Стиль текста",
        extra_fields: "Добавить в пост",
        framework: "Фреймворк",
        post_num: "Количество постов",
        language: "Язык",
      }[key];

      if (!value || !label) {
        return undefined;
      }

      return `${label}: ${value}`;
    }),
  ).join("\n");

  if (!contextParamsString && !postParamsString) {
    return "";
  }

  let result = `Контекст бизнеса/услуги и настройки поста\n\n`;

  if (contextParamsString) {
    result += `Контекст\n${contextParamsString}`;
  }

  if (postParamsString) {
    result += `\n\nНастройки поста\n${postParamsString}`;
  }

  return result;
};
